import upperFirst from 'lodash/upperFirst'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import { GraphStackResponse } from 'src/api/gen'
import { exportGraphData } from 'src/commons/export/Export'
import BaseChartWrapper from 'src/commons/graphs/BaseChartWrapper'
import { DashboardGraphWrapper } from 'src/commons/graphs/GraphWrapper/GraphWrappers'
import { userHasPermission } from 'src/commons/permissions'
import { IFilters, IGraphComponent } from 'src/commons/types'
import { createPayloadFromFilters } from 'src/commons/utils'
import WarnBanner from 'src/components/WarnBanner'
import { useProviders, useProviderUser } from 'src/layout/hooks'

import {
  OffensePenaltyPerProviderPerDate,
  OffensesPerProviderPerDate,
  OffensesPerKindPerDate,
  OffensesPerKindPerDatePercentage,
} from './charts'
import { formatExportOffensesPerKindPerDate } from './exports'

function OffensePenaltyPerProviderPerDateComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const isProviderUser = useProviderUser()
  const { activeProviders } = useProviders()

  async function fetcher() {
    const data = createPayloadFromFilters(props.filters)
    return await api.graphs.graphOffensePricePerProviderPerDate({ data })
  }

  function render(data: any, title?: string) {
    return <OffensePenaltyPerProviderPerDate title={title} graphReport={data} granularity={props.filters.granularity} />
  }

  let customExport
  if (userHasPermission(['analyze', 'policy'])) {
    customExport = exportGraphData(props.filters.granularity)
  }

  let title
  let description
  if (isProviderUser && activeProviders.length < 2) {
    title = upperFirst(t('kpi.penaltyPerDate.title'))
    description = upperFirst(t('kpi.penaltyPerDate.description'))
  } else {
    title = upperFirst(t('kpi.offenses.penaltyPerProviderPerDate.title'))
    description = upperFirst(t('kpi.offenses.penaltyPerProviderPerDate.description'))
  }

  return (
    <DashboardGraphWrapper
      updateOn={[props.filters]}
      title={title}
      description={description}
      fetcher={fetcher}
      render={render}
      customExport={customExport}
    />
  )
}

function OffensesPerProviderPerDateComponant(props: IGraphComponent) {
  const { t } = useTranslation()

  const isProviderUser = useProviderUser()
  const { activeProviders } = useProviders()

  async function fetcher() {
    const data = createPayloadFromFilters(props.filters)
    return await api.graphs.graphOffensesPerProviderPerDate({ data })
  }

  function render(data: any, title?: string) {
    return <OffensesPerProviderPerDate title={title} graphReport={data} granularity={props.filters.granularity} />
  }

  let customExport
  if (userHasPermission(['analyze', 'policy'])) {
    // FIXME(xfernandez, 2023-03-30) : export date format is not clean
    customExport = exportGraphData(props.filters.granularity)
  }

  let title
  let description
  if (isProviderUser && activeProviders.length < 2) {
    title = upperFirst(t('kpi.offenses.offensesPerDate.title'))
    description = upperFirst(t('kpi.offenses.offensesPerDate.description'))
  } else {
    title = upperFirst(t('kpi.offenses.offensesPerProviderPerDate.title'))
    description = upperFirst(t('kpi.offenses.offensesPerProviderPerDate.description'))
  }

  return (
    <DashboardGraphWrapper
      updateOn={[props.filters]}
      title={title}
      description={description}
      fetcher={fetcher}
      render={render}
      customExport={customExport}
    />
  )
}

function OffensesPerKindPerDateComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const [percentMode, setPercentMode] = useState<boolean>(true)

  async function fetcher(): Promise<GraphStackResponse[]> {
    const data = createPayloadFromFilters(props.filters)
    if (percentMode) {
      return api.graphs.graphPercentageOfOffensesPerKind({ data })
    }
    return api.graphs.graphOffensesPerKind({ data })
  }
  function render(data: GraphStackResponse[], title?: string) {
    if (percentMode) {
      return (
        <OffensesPerKindPerDatePercentage title={title} graphReport={data} granularity={props.filters.granularity} />
      )
    } else {
      return <OffensesPerKindPerDate title={title} graphReport={data} granularity={props.filters.granularity} />
    }
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'policy'])) {
    formatExportBuckets = formatExportOffensesPerKindPerDate(props.filters.granularity, percentMode)
  }

  return (
    <DashboardGraphWrapper
      title={
        percentMode
          ? upperFirst(t('kpi.OffensesPerKindPerDatePercentage.title'))
          : upperFirst(t('kpi.OffensesPerKindPerDate.title'))
      }
      description={
        percentMode
          ? upperFirst(t('kpi.OffensesPerKindPerDatePercentage.description'))
          : upperFirst(t('kpi.OffensesPerKindPerDate.description'))
      }
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
      toggles={[
        {
          active: percentMode,
          onToggle: () => setPercentMode(!percentMode),
          activeIcon: 'toggle-off',
          inactiveIcon: 'toggle-on',
          activeText: t('kpi.OffensesPerKindPerDate.activeText'),
          inactiveText: t('kpi.OffensesPerKindPerDate.inactiveText'),
        },
      ]}
      updateOn={[percentMode, props.filters]}
    />
  )
}

interface IComplianceCharts {
  filters: IFilters
}

function ComplianceCharts(props: IComplianceCharts) {
  return (
    <BaseChartWrapper>
      <WarnBanner toDate={props.filters.range.to} />
      <OffensesPerProviderPerDateComponant id="offenses-per-provider" filters={props.filters} />
      <OffensePenaltyPerProviderPerDateComponent filters={props.filters} />
      <OffensesPerKindPerDateComponent filters={props.filters} />
    </BaseChartWrapper>
  )
}

export default ComplianceCharts
